import { OrderData } from 'order.d';

import { TOPSORT_URL_EVENTS } from 'constants/topsort';
import { browserLogger } from 'utils/logger/browserLogger';
import { generateUUID } from 'utils/generateUUID';
import { getExistingTopsortUserId } from './getTopsortUserId';

const getTopsortToken = (): string => {
  if (process.env.NEXT_PUBLIC_TOPSORT_TOKEN) {
    return process.env.NEXT_PUBLIC_TOPSORT_TOKEN;
  }
  if (typeof window !== 'undefined') {
    return window.TS?.token || '';
  }
  return '';
};

export async function sendTopsortPurchase(
  orderData: OrderData,
  isMobile: boolean
) {
  try {
    const token = getTopsortToken();

    if (!orderData || !token) {
      browserLogger().error({
        message: `Failed to send Topsort Purchase event. Missing ${!orderData ? 'order data' : 'Topsort token'}.`,
        data: !orderData ? 'Order data is undefined or null.' : 'Topsort token is missing.'
      });
      return;
    }
    const topsortUserId = getExistingTopsortUserId();

    if (!topsortUserId) {
      browserLogger().error({
        message: `Failed to send Topsort Purchase event.`,
        data: 'Unable to retrieve Topsort User ID.'
      });
      return;
    }

    const payload = {
      purchases: [
        {
          occurredAt: new Date().toISOString(),
          opaqueUserId: topsortUserId,
          items: [
            {
              productId: orderData.eventId,
              unitPrice: 1
            }
          ],
          id: orderData.orderNumber,
          deviceType: isMobile ? 'mobile' : 'desktop'
        }
      ]
    };

    const responseAPI = await fetch(TOPSORT_URL_EVENTS, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (responseAPI.status !== 204) {
      const errorBody = await responseAPI.text();
      throw new Error(
        `Topsort API error! status: ${responseAPI.status}. Details: ${errorBody}`
      );
    }
  } catch (error) {
    let errorMessage = 'An unknown error occurred';

    if (error instanceof Error) {
      errorMessage = error.message;
    }

    browserLogger().error({
      message: `Failed to send Topsort Purchase event: ${errorMessage}`,
      data: String(error)
    });
  }
}

interface TopsortEventParams {
  type: 'impression' | 'click';
  resolvedBidId: string;
  idBanner: string;
  additionalAttribution?: { id: string; type: 'product' };
}

interface TopsortEventData {
  id: string;
  resolvedBidId: string;
  opaqueUserId: string;
  occurredAt: string;
  placement: {
    path: string;
    position: number;
  };
  additionalAttribution?: { id: string; type: 'product' };
}

export const sendTopsortEvent = ({
  type,
  resolvedBidId,
  idBanner,
  additionalAttribution
}: TopsortEventParams) => {
  try {
    if (typeof window === 'undefined') return;

    const token = getTopsortToken();

    if (!token) {
      browserLogger().error({
        message: `Failed to send Topsort ${type} event. Missing Topsort token.`,
        data: 'Topsort token is missing from environment variables or global window object.'
      });
      return;
    }

    const topsortUserId = getExistingTopsortUserId();

    if (!topsortUserId) {
      browserLogger().error({
        message: `Failed to send Topsort ${type} event.`,
        data: 'Unable to retrieve Topsort User ID.'
      });
      return;
    }

    const match = idBanner.match(/_(\d+)$/);
    const position = match ? Number(match[1]) : 1;

    const eventData: TopsortEventData = {
      id: generateUUID(),
      resolvedBidId,
      opaqueUserId: topsortUserId,
      occurredAt: new Date().toISOString(),
      placement: {
        path: window.location.pathname,
        position
      }
    };

    if (type === 'click' && additionalAttribution) {
      eventData.additionalAttribution = additionalAttribution;
    }

    const payload =
      type === 'impression'
        ? { impressions: [eventData] }
        : { clicks: [eventData] };

    fetch(TOPSORT_URL_EVENTS, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      keepalive: true
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(`HTTP Error ${response.status}: ${errorBody}`);
        }
      })
      .catch((err) => {
        browserLogger().error({
          message: `Topsort ${type} event fetch error`,
          data: err instanceof Error ? err.message : String(err)
        });
      });
  } catch (err) {
    browserLogger().error({
      message: `Topsort ${type} event execution error`,
      data: String(err)
    });
  }
};
